import { useQuery } from "@tanstack/react-query";
import { type Address } from "viem";

import { MORPHO_CADC_USDC_MARKET_ID, VENUES } from "@/config/contracts";
import {
  MORPHO_BLUE_ABI,
  MORPHO_IRM_ABI,
  MORPHO_ORACLE_ABI,
} from "@/config/morpho-abi";
import { buildPublicClient } from "@/lib/wallet/clients";

export type MorphoMarketParams = {
  readonly loanToken: Address;
  readonly collateralToken: Address;
  readonly oracle: Address;
  readonly irm: Address;
  readonly lltv: bigint;
};

export type MorphoMarketState = {
  readonly totalSupplyAssets: bigint;
  readonly totalBorrowAssets: bigint;
  readonly totalSupplyShares: bigint;
  readonly totalBorrowShares: bigint;
  readonly lastUpdate: bigint;
  readonly fee: bigint;
};

export type MorphoMarketData = {
  readonly params: MorphoMarketParams;
  readonly state: MorphoMarketState;
  readonly oraclePrice: bigint;
  /** Borrow rate per second, scaled 1e18. */
  readonly borrowRatePerSec: bigint;
  /** Borrow APR as a decimal (0.085 = 8.5%). */
  readonly borrowApr: number;
  /** Loan-token units available to borrow now. */
  readonly availableLiquidity: bigint;
};

const SECONDS_PER_YEAR = 365 * 24 * 60 * 60;

/** Cached for the lifetime of the tab — Morpho market params are immutable. */
let cachedParams: MorphoMarketParams | null = null;

const fetchParams = async (): Promise<MorphoMarketParams> => {
  if (cachedParams) return cachedParams;
  const client = buildPublicClient();
  const tuple = await client.readContract({
    address: VENUES.morpho.contract,
    abi: MORPHO_BLUE_ABI,
    functionName: "idToMarketParams",
    args: [MORPHO_CADC_USDC_MARKET_ID],
  });
  cachedParams = {
    loanToken: tuple[0],
    collateralToken: tuple[1],
    oracle: tuple[2],
    irm: tuple[3],
    lltv: tuple[4],
  };
  return cachedParams;
};

export const useMorphoMarket = () =>
  useQuery({
    queryKey: ["morpho-market", MORPHO_CADC_USDC_MARKET_ID],
    queryFn: async (): Promise<MorphoMarketData> => {
      const client = buildPublicClient();
      const params = await fetchParams();

      // Single multicall: market state + oracle price.
      const [marketTuple, oraclePrice] = await client.multicall({
        allowFailure: false,
        contracts: [
          {
            address: VENUES.morpho.contract,
            abi: MORPHO_BLUE_ABI,
            functionName: "market",
            args: [MORPHO_CADC_USDC_MARKET_ID],
          },
          {
            address: params.oracle,
            abi: MORPHO_ORACLE_ABI,
            functionName: "price",
          },
        ],
      });

      const state: MorphoMarketState = {
        totalSupplyAssets: marketTuple[0],
        totalSupplyShares: marketTuple[1],
        totalBorrowAssets: marketTuple[2],
        totalBorrowShares: marketTuple[3],
        lastUpdate: marketTuple[4],
        fee: marketTuple[5],
      };

      // Borrow rate depends on the just-read market state.
      const borrowRatePerSec = await client.readContract({
        address: params.irm,
        abi: MORPHO_IRM_ABI,
        functionName: "borrowRateView",
        args: [
          {
            loanToken: params.loanToken,
            collateralToken: params.collateralToken,
            oracle: params.oracle,
            irm: params.irm,
            lltv: params.lltv,
          },
          {
            totalSupplyAssets: state.totalSupplyAssets,
            totalSupplyShares: state.totalSupplyShares,
            totalBorrowAssets: state.totalBorrowAssets,
            totalBorrowShares: state.totalBorrowShares,
            lastUpdate: state.lastUpdate,
            fee: state.fee,
          },
        ],
      });

      const ratePerSecAsNumber = Number(borrowRatePerSec) / 1e18;
      const borrowApr = ratePerSecAsNumber * SECONDS_PER_YEAR;

      const availableLiquidity =
        state.totalSupplyAssets > state.totalBorrowAssets
          ? state.totalSupplyAssets - state.totalBorrowAssets
          : 0n;

      return {
        params,
        state,
        oraclePrice,
        borrowRatePerSec,
        borrowApr,
        availableLiquidity,
      };
    },
  });
